/**
 * Markdown → sanitised HTML pipeline for pedagogical content.
 *
 * Security model
 * --------------
 * Course content is authored by instructors and administrators, but it is still
 * rendered as HTML in every learner's browser. A single `<script>` or
 * `onerror=` attribute would be a stored XSS affecting every reader, so the
 * output of `marked` NEVER reaches the DOM directly:
 *
 *   1. `marked` converts Markdown to HTML (no raw-HTML trust assumption);
 *   2. DOMPurify strips everything outside an explicit allow-list — no script,
 *      no event handler, no `javascript:` URL, no iframe, no form;
 *   3. a DOMPurify hook forces every external link to open safely
 *      (`rel="noreferrer noopener"`), which also prevents tab-nabbing.
 *
 * Mermaid diagrams are extracted *before* sanitisation and rendered separately
 * by the Mermaid library (`securityLevel: 'strict'`), so diagram source is never
 * injected as HTML either.
 */

import DOMPurify from 'dompurify';
import hljs from 'highlight.js/lib/common';
import { Marked } from 'marked';

export interface TocEntry {
  id: string;
  text: string;
  level: 2 | 3;
}

export interface RenderedMarkdown {
  html: string;
  toc: TocEntry[];
  /** Mermaid sources, keyed by the placeholder id present in the HTML. */
  diagrams: Record<string, string>;
  /**
   * The document's own leading `# Title`, lifted out of the HTML.
   *
   * Chapters, lab statements and corrections all open with their title, but the
   * surrounding page already renders it as the page's single `<h1>`. Emitting it
   * twice would give the page two `<h1>` elements — a document-outline defect for
   * screen readers — and show the title twice. The caller can use this value to
   * check it matches what it displays.
   */
  title: string | null;
}

const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'hr', 'div', 'span',
  'strong', 'em', 'del', 'ins', 'sub', 'sup', 'mark', 'abbr',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code', 'kbd', 'samp',
  'a', 'img',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'details', 'summary', 'figure', 'figcaption',
];

const ALLOWED_ATTR = [
  'href', 'title', 'alt', 'src', 'id', 'class', 'colspan', 'rowspan',
  'start', 'reversed', 'open', 'lang', 'dir', 'width', 'height',
];

/** Prefix of the `id` marking a Mermaid placeholder in the sanitised HTML. */
export const MERMAID_ID_PREFIX = 'kha-mermaid-';

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Rendering is stateful (TOC, diagrams), so a fresh instance is built per call. */
function createRenderer() {
  const toc: TocEntry[] = [];
  const diagrams: Record<string, string> = {};
  const usedIds = new Set<string>();
  let diagramIndex = 0;
  const state: { title: string | null } = { title: null };

  const marked = new Marked({
    gfm: true,
    breaks: false,
    // Raw HTML in the source is still parsed by marked, but DOMPurify is what
    // decides what survives — we never rely on marked for safety.
    async: false,
  });

  marked.use({
    renderer: {
      heading({ tokens, depth }) {
        const text = this.parser.parseInline(tokens);
        const plain = text.replace(/<[^>]+>/g, '');

        // The document's own title is captured, not rendered: the page provides
        // the single <h1>. Any further <h1> is demoted to keep the outline sane.
        if (depth === 1) {
          if (state.title === null) {
            state.title = plain;
            return '';
          }
          depth = 2;
        }

        let id = slugify(plain) || `section-${toc.length + 1}`;
        // Never collide with a Mermaid placeholder id.
        if (id.startsWith(MERMAID_ID_PREFIX)) id = `section-${id}`;
        let suffix = 2;
        while (usedIds.has(id)) id = `${slugify(plain)}-${suffix++}`;
        usedIds.add(id);
        if (depth === 2 || depth === 3) toc.push({ id, text: plain, level: depth });
        return `<h${depth} id="${id}">${text}</h${depth}>`;
      },

      code({ text, lang }) {
        const language = (lang ?? '').trim().split(/\s+/)[0] ?? '';

        if (language === 'mermaid') {
          // Extracted here: the diagram source never goes through the HTML path.
          // The placeholder is marked with `id`, not a data attribute: keeping
          // ALLOW_DATA_ATTR off is worth more than the nicer attribute name, and
          // DOMPurify drops data-* even when they are listed in ALLOWED_ATTR.
          const id = `${MERMAID_ID_PREFIX}${diagramIndex++}`;
          diagrams[id] = text;
          return `<div class="kha-mermaid" id="${id}"></div>`;
        }

        let highlighted: string;
        try {
          highlighted =
            language && hljs.getLanguage(language)
              ? hljs.highlight(text, { language, ignoreIllegals: true }).value
              : escapeHtml(text);
        } catch {
          // A failing highlighter must never break the page: fall back to plain.
          highlighted = escapeHtml(text);
        }
        return (
          `<pre data-lang="${escapeHtml(language)}">` +
          `<code class="hljs language-${escapeHtml(language)}">${highlighted}</code>` +
          `</pre>`
        );
      },

      table(token) {
        // Wrapped so a wide table scrolls instead of breaking the layout.
        let header = '';
        for (const cell of token.header) {
          header += `<th${cell.align ? ` class="text-${cell.align}"` : ''}>${this.parser.parseInline(
            cell.tokens,
          )}</th>`;
        }
        let body = '';
        for (const row of token.rows) {
          let cells = '';
          for (const cell of row) {
            cells += `<td${cell.align ? ` class="text-${cell.align}"` : ''}>${this.parser.parseInline(
              cell.tokens,
            )}</td>`;
          }
          body += `<tr>${cells}</tr>`;
        }
        return (
          `<div class="kha-table-wrap"><table>` +
          `<thead><tr>${header}</tr></thead><tbody>${body}</tbody>` +
          `</table></div>`
        );
      },
    },
  });

  return { marked, toc, diagrams, state };
}

let hooksInstalled = false;

function installPurifyHooks(): void {
  if (hooksInstalled) return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node instanceof Element && node.tagName === 'A') {
      const href = node.getAttribute('href') ?? '';
      // External links open in a new tab with no referrer and no window.opener.
      if (/^https?:\/\//i.test(href)) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noreferrer noopener');
      } else if (!href.startsWith('#') && !href.startsWith('/')) {
        // Anything that is not http(s), an anchor or an internal path is dropped.
        node.removeAttribute('href');
      }
    }
    if (node instanceof Element && node.tagName === 'IMG') {
      node.setAttribute('loading', 'lazy');
      node.setAttribute('decoding', 'async');
    }
  });
  hooksInstalled = true;
}

export function renderMarkdown(source: string): RenderedMarkdown {
  installPurifyHooks();
  const { marked, toc, diagrams, state } = createRenderer();
  const raw = marked.parse(source) as string;

  const html = DOMPurify.sanitize(raw, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    // Defence in depth: these are already excluded by the allow-list.
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'link', 'meta'],
    FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'srcset', 'formaction'],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|#|\/)/i,
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
  });

  return { html, toc, diagrams, title: state.title };
}

/** Extracts the commands of a `pre` block so a copy button can use them. */
export function textOfCodeBlock(element: Element): string {
  return element.querySelector('code')?.textContent ?? '';
}
